import { useState, useEffect } from 'react';
import { motion, Reorder } from 'framer-motion';
import { Link } from 'react-router-dom';
import { PageTransition, FadeIn } from '../components/layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  ChevronRight, Plus, Edit2, Trash2, Loader2, FileText,
  GripVertical, ToggleLeft, ToggleRight, ArrowLeft, Save, Download, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toAbsoluteUrl } from '../lib/utils';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminB2BDocumentsPage() {
  const { token } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasOrderChanges, setHasOrderChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  
  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    file_url: '',
    is_active: true
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/b2b/admin/documents`, { headers });
      setDocuments(response.data.documents || []);
      setHasOrderChanges(false);
    } catch (err) {
      console.error('Error fetching documents:', err);
      toast.error('Erreur lors du chargement des documents');
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingDoc(null);
    setFormData({ title: '', description: '', file_url: '', is_active: true });
    setIsDialogOpen(true);
  };

  const openEditDialog = (doc) => {
    setEditingDoc(doc);
    setFormData({
      title: doc.title,
      description: doc.description || '',
      file_url: doc.file_url,
      is_active: doc.is_active
    });
    setIsDialogOpen(true);
  };

  const handleSaveDocument = async () => {
    if (!formData.title.trim()) {
      toast.error('Le titre est requis');
      return;
    }
    if (!formData.file_url.trim()) {
      toast.error('L\'URL du fichier est requise');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        title: formData.title,
        description: formData.description || null,
        file_url: formData.file_url,
        is_active: formData.is_active
      };

      if (editingDoc) {
        await axios.put(`${API_URL}/b2b/admin/documents/${editingDoc.id}`, payload, { headers });
        toast.success('Document modifié');
      } else {
        await axios.post(`${API_URL}/b2b/admin/documents`, payload, { headers });
        toast.success('Document créé');
      }
      setIsDialogOpen(false);
      fetchDocuments();
    } catch (err) {
      console.error('Error saving document:', err);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteDocument = async (doc) => {
    if (!window.confirm(`Supprimer le document "${doc.title}" ?`)) return;
    
    try {
      await axios.delete(`${API_URL}/b2b/admin/documents/${doc.id}`, { headers });
      toast.success('Document supprimé');
      fetchDocuments();
    } catch (err) {
      console.error('Error deleting document:', err);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggleDocument = async (doc) => {
    try {
      await axios.patch(`${API_URL}/b2b/admin/documents/${doc.id}/toggle`, {}, { headers });
      toast.success(doc.is_active ? 'Document désactivé' : 'Document activé');
      fetchDocuments();
    } catch (err) {
      console.error('Error toggling document:', err);
      toast.error('Erreur lors de la modification');
    }
  };

  const handleReorderDocuments = (newOrder) => {
    setDocuments(newOrder);
    setHasOrderChanges(true);
  };

  const saveDocumentsOrder = async () => {
    setIsSaving(true);
    try {
      await axios.post(`${API_URL}/b2b/admin/documents/reorder`, {
        document_ids: documents.map(d => d.id)
      }, { headers });
      toast.success('Ordre enregistré');
      setHasOrderChanges(false);
    } catch (err) {
      console.error('Error saving order:', err);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const payload = new FormData();
    payload.append('file', file);

    setIsUploadingFile(true);
    try {
      const response = await axios.post(`${API_URL}/uploads/b2b-document`, payload, { headers });
      if (response.data?.file_url) {
        setFormData(prev => ({ ...prev, file_url: response.data.file_url }));
        toast.success('Document uploadé');
      } else {
        toast.error('Réponse upload invalide');
      }
    } catch (err) {
      console.error('Error uploading document:', err);
      toast.error('Erreur lors de l\'upload du document');
    } finally {
      setIsUploadingFile(false);
    }
  };

  return (
    <PageTransition>
      {/* Hero */}
      <section className="pt-32 pb-12 px-6 bg-[var(--charcoal)]">
        <div className="max-w-[1400px] mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light mb-4 text-white"
          >
            Documents B2B
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-white/60"
          >
            Gérez les documents téléchargeables pour la page Atelier Événementiel
          </motion.p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-[var(--champagne)]">
        <div className="max-w-[1400px] mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-[var(--charcoal)]/60">
            <Link to="/admin" className="hover:text-[var(--gold)]">Admin</Link>
            <ChevronRight size={14} />
            <span className="text-[var(--charcoal)]">Documents B2B</span>
          </nav>
        </div>
      </div>

      {/* Content */}
      <section className="py-12 px-6">
        <div className="max-w-[800px] mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <Button
              variant="ghost"
              onClick={() => window.history.back()}
              className="gap-2"
            >
              <ArrowLeft size={16} />
              Retour
            </Button>
            <div className="flex gap-2">
              {hasOrderChanges && (
                <Button
                  onClick={saveDocumentsOrder}
                  disabled={isSaving}
                  className="bg-[var(--gold)] text-white hover:bg-[var(--dark-gold)] rounded-none gap-2"
                >
                  {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  Enregistrer l'ordre
                </Button>
              )}
              <Button
                onClick={openCreateDialog}
                className="bg-[var(--gold)] text-white hover:bg-[var(--dark-gold)] rounded-none gap-2"
              >
                <Plus size={16} />
                Nouveau document
              </Button>
            </div>
          </div>

          {/* Documents List */}
          {isLoading ? (
            <div className="flex justify-center py-20">
              <Loader2 size={32} className="animate-spin text-[var(--gold)]" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-16 bg-[var(--champagne)]/20 border border-[var(--champagne)]">
              <FileText size={48} className="mx-auto mb-4 text-[var(--gold)]/50" />
              <p className="text-[var(--charcoal)]/60 mb-4">Aucun document B2B</p>
              <Button onClick={openCreateDialog} className="bg-[var(--gold)] text-white hover:bg-[var(--dark-gold)] rounded-none">
                Créer le premier document
              </Button>
            </div>
          ) : (
            <Reorder.Group
              axis="y"
              values={documents}
              onReorder={handleReorderDocuments}
              className="space-y-3"
            >
              {documents.map((doc) => (
                <Reorder.Item
                  key={doc.id}
                  value={doc}
                  className={`bg-white border border-[var(--champagne)] p-4 flex items-center justify-between cursor-grab active:cursor-grabbing ${!doc.is_active ? 'opacity-50' : ''}`}
                  whileDrag={{ scale: 1.02, boxShadow: "0 10px 30px rgba(0,0,0,0.15)" }}
                >
                  <div className="flex items-center gap-4">
                    <GripVertical size={18} className="text-[var(--charcoal)]/30" />
                    <div className="w-10 h-10 bg-[var(--champagne)]/50 flex items-center justify-center">
                      <FileText size={20} className="text-[var(--gold)]" />
                    </div>
                    <div>
                      <p className="font-medium text-[var(--charcoal)]">{doc.title}</p>
                      {doc.description && (
                        <p className="text-sm text-[var(--charcoal)]/60">{doc.description}</p>
                      )}
                      <a 
                        href={toAbsoluteUrl(doc.file_url)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-[var(--gold)] hover:underline flex items-center gap-1 mt-1"
                      >
                        <ExternalLink size={12} />
                        Voir le fichier
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleDocument(doc)}
                      title={doc.is_active ? 'Désactiver' : 'Activer'}
                    >
                      {doc.is_active ? (
                        <ToggleRight size={18} className="text-green-500" />
                      ) : (
                        <ToggleLeft size={18} className="text-[var(--charcoal)]/30" />
                      )}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openEditDialog(doc)}>
                      <Edit2 size={16} className="text-[var(--charcoal)]/60" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteDocument(doc)}>
                      <Trash2 size={16} className="text-red-400" />
                    </Button>
                  </div>
                </Reorder.Item>
              ))}
            </Reorder.Group>
          )}

          {/* Info Box */}
          <FadeIn delay={0.2}>
            <div className="mt-8 p-6 bg-[var(--champagne)]/30 border border-[var(--champagne)]">
              <h3 className="font-medium text-[var(--charcoal)] mb-2 flex items-center gap-2">
                <Download size={16} className="text-[var(--gold)]" />
                À propos des documents B2B
              </h3>
              <ul className="text-sm text-[var(--charcoal)]/70 space-y-1">
                <li>• Ces documents seront affichés sur la page Atelier Événementiel</li>
                <li>• Vous pouvez uploader un PDF ou utiliser une URL externe</li>
                <li>• Glissez-déposez pour réorganiser l'ordre d'affichage</li>
                <li>• Seuls les documents actifs sont visibles sur le site public</li>
              </ul>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Dialog for Create/Edit Document */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-xl">
              {editingDoc ? 'Modifier le document' : 'Nouveau document'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Titre du bouton <span className="text-red-500">*</span></Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="ex: Télécharger la plaquette entreprise"
              />
            </div>

            <div>
              <Label>Uploader un PDF</Label>
              <Input
                type="file"
                accept="application/pdf"
                onChange={handleFileUpload}
              />
              {isUploadingFile && (
                <p className="text-xs text-[var(--charcoal)]/60 mt-2 flex items-center gap-2">
                  <Loader2 size={12} className="animate-spin" />
                  Upload en cours...
                </p>
              )}
              <div className="mt-3">
                <Label>URL du fichier <span className="text-red-500">*</span></Label>
                <Input
                  value={formData.file_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, file_url: e.target.value }))}
                  placeholder="https://exemple.com/document.pdf"
                />
                <p className="text-xs text-[var(--charcoal)]/50 mt-1">
                  URL locale (/media/...) ou externe
                </p>
              </div>
            </div>

            <div>
              <Label>Description (optionnelle)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description interne pour l'admin"
                className="min-h-[80px]"
              />
            </div>

            <div className="flex items-center justify-between pt-2">
              <Label>Actif</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              className="rounded-none"
            >
              Annuler
            </Button>
            <Button
              onClick={handleSaveDocument}
              disabled={isSaving}
              className="bg-[var(--gold)] text-white hover:bg-[var(--dark-gold)] rounded-none"
            >
              {isSaving ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              {editingDoc ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageTransition>
  );
}
